import { useCallback, useEffect, useMemo, useState } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { NavigateOptions, useLocation, useNavigate } from 'react-router-dom';
import { AppId } from './appTypes';
import type { RootState, AppDispatch } from './store';
import { deDE, enUS, plPL } from "@mui/x-data-grid/locales";
import { selectSettings } from '../features/settings/settingsSlice';
import { setStructureAuto } from '../features/structure/contextStructureSlice';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Trails `value` by `delayMs`, resetting the timer on every change. Useful to keep
// expensive derived work off the per-keystroke path of a rich text editor.
// Pass `resetKey` when the subject being edited can be swapped out underneath: the
// value is then adopted immediately instead of trailing the previous subject.
export function useDebouncedValue<T>(value: T, delayMs: number, resetKey?: unknown): T {
    const [debounced, setDebounced] = useState(value)
    const [seenKey, setSeenKey] = useState(resetKey)

    if (seenKey !== resetKey) {
        setSeenKey(resetKey)
        setDebounced(value)
    }

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delayMs)
        return () => clearTimeout(timer)
    }, [value, delayMs])

    return debounced
}

export const useDocNavigator = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    return useCallback((url: string, structureId: AppId, options?: NavigateOptions) => {
        dispatch(setStructureAuto(structureId))
        navigate(url, options)
    }, [])
}

export const useQuery = () => {
    const { search } = useLocation()

    return useMemo(() => new URLSearchParams(search), [search])
}

export const useQueryId = (): AppId | undefined => {
    const query = useQuery()

    if (query.has("id")) {
        return query.get("id") ?? undefined
    } else {
        return undefined
    }
}

export const useQueryCompanyToRegister = (): AppId | undefined => {
    const query = useQuery()

    if (query.has("companyToRegister")) {
        return query.get("companyToRegister") ?? undefined
    } else {
        return undefined
    }
}

export const useQueryCompanyId = (): AppId | undefined => {
    const query = useQuery()

    if (query.has("companyId")) {
        return query.get("companyId") ?? undefined
    } else {
        return undefined
    }
}

export const useQueryProjectId = (): AppId | undefined => {
    const query = useQuery()

    if (query.has("projectId")) {
        return query.get("projectId") ?? undefined
    } else {
        return undefined
    }
}

export const useQueryBusinessCaseId = (): AppId | undefined => {
    const query = useQuery()

    if (query.has("businessCaseId")) {
        return query.get("businessCaseId") ?? undefined
    } else {
        return undefined
    }
}


export const useDataGridCustomLocaleText = () => {
    const { locale } = useAppSelector(selectSettings);

    return (locale === "pl" ? plPL : locale === "de" ? deDE : enUS)
        .components.MuiDataGrid.defaultProps.localeText;
};
